import type { components } from "@/api/schema";

type Promotion = components["schemas"]["Promotion"];

export interface LinkedDraftGroupRow {
  kind: "linked-draft-group";
  groupId: string;
  members: Promotion[];
}

export type PromotionRow = Promotion | LinkedDraftGroupRow;

export function isLinkedDraftGroupRow(row: PromotionRow): row is LinkedDraftGroupRow {
  return "kind" in row && row.kind === "linked-draft-group";
}

/**
 * Collapses promotions that share a non-null linked_group_id — and are ALL
 * still draft — into one LinkedDraftGroupRow, so the list shows "one thing
 * being edited" instead of N half-finished rows (ADR-0021). A group is
 * shown as normal independent rows the moment ANY member is no longer
 * draft (defensive: the backend clears linked_group_id on activation, so
 * this should never actually fire in practice, but the list must never
 * silently hide an active/finished promotion inside a "draft" grouping).
 *
 * Row order mirrors `items` (the backend's sort order, e.g. by
 * created_at): a collapsed group row is emitted at the position of its
 * FIRST member's index, not shoved to the front/back of the list.
 */
export function groupLinkedDrafts(items: Promotion[]): PromotionRow[] {
  const byGroup = new Map<string, Promotion[]>();
  for (const p of items) {
    if (!p.linked_group_id) continue;
    const bucket = byGroup.get(p.linked_group_id) ?? [];
    bucket.push(p);
    byGroup.set(p.linked_group_id, bucket);
  }

  const collapses = (groupId: string): boolean => {
    const members = byGroup.get(groupId) ?? [];
    return members.length > 1 && members.every((m) => m.status === "draft");
  };

  const rows: PromotionRow[] = [];
  const emittedGroups = new Set<string>();
  for (const p of items) {
    if (p.linked_group_id && collapses(p.linked_group_id)) {
      if (emittedGroups.has(p.linked_group_id)) continue;
      emittedGroups.add(p.linked_group_id);
      rows.push({
        kind: "linked-draft-group",
        groupId: p.linked_group_id,
        members: byGroup.get(p.linked_group_id) ?? [],
      });
    } else {
      rows.push(p);
    }
  }
  return rows;
}
